import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Plus, 
  Code, 
  FileCode, 
  Brush, 
  FileText, 
  Book, 
  Download, 
  Delete, 
  Image,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Attachment } from '@/types';
import ChatInputField from './ChatInputField';
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases';
import KnowledgeBaseActiveIndicator from './KnowledgeBaseActiveIndicator';
import { useChatInput } from '@/hooks/useChatInput';
import InputAttachmentPreview from './InputAttachmentPreview';
import { toast } from '@/hooks/use-toast';
import { readFileContent } from '@/utils/chatUtils';

interface SimpleChatInputProps {
  onSendMessage: (message: string, attachments?: Attachment[], kbId?: string | null, kbName?: string | null) => void;
  onClearChat?: () => void;
  isLoading: boolean;
  onOpenCodeEditor?: () => void;
  onOpenCanvas?: () => void;
  onOpenCodeHistory?: () => void;
  onFileUpload?: (file: File) => void;
  onExportChat?: () => void;
  chatId?: string;
}

const SimpleChatInput: React.FC<SimpleChatInputProps> = ({
  onSendMessage,
  onClearChat,
  isLoading,
  onOpenCodeEditor,
  onOpenCanvas,
  onOpenCodeHistory,
  onFileUpload,
  onExportChat,
  chatId
}) => {
  const { knowledgeBases, loading: kbLoading, fetchKnowledgeBases } = useKnowledgeBases();
  const [isUploading, setIsUploading] = useState(false);
  
  const {
    message,
    pendingAttachments,
    setPendingAttachments,
    textareaRef,
    fileInputRef,
    activeKnowledgeBase,
    handleSubmit,
    handleKeyDown,
    handleTextareaChange,
    handleFileSelect,
    handleFileChange: baseHandleFileChange,
    handleRemoveAttachment,
    handleSelectKnowledgeBase,
    clearKnowledgeBase,
    setMessage,
  } = useChatInput(
    onSendMessage,
    onClearChat,
    onOpenCodeEditor,
    onOpenCanvas,
    onExportChat,
    isLoading,
    chatId
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Maximum file size is 10 MB",
          variant: "destructive"
        });
        return;
      }
      
      const isImage = file.type.startsWith('image/');
      const isDocument = file.type === 'application/pdf' || 
                      file.type === 'text/plain' || 
                      file.type === 'text/csv' || 
                      file.name.endsWith('.pdf') || 
                      file.name.endsWith('.txt') || 
                      file.name.endsWith('.csv') || 
                      file.name.endsWith('.docx');
      
      if (!isImage && !isDocument) {
        toast({
          title: "Unsupported file type",
          description: "Please upload an image, PDF, TXT, CSV, or DOCX file",
          variant: "destructive"
        });
        return;
      }
      
      setIsUploading(true);
      try {
        let content: string | null = null;
        if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
          content = await readFileContent(file);
          if (!content || content.trim().length === 0) {
            content = `[This is a PDF file: ${file.name}]\n\nUnable to extract text content from this PDF.`;
          }
        } else if (isDocument) {
          content = await readFileContent(file);
        } else {
          const reader = new FileReader();
          content = await new Promise<string>((resolve, reject) => {
            reader.onload = (event) => {
              if (event.target?.result) {
                resolve(event.target.result as string);
              } else {
                reject(new Error('Failed to read file'));
              }
            };
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
          });
        }

        if (file.type === 'application/pdf' || file.name.endsWith('.pdf') || isDocument) {
          const newAttachment: Attachment = {
            id: Math.random().toString(36).substring(2, 11),
            type: 'file',
            content: content || '',
            name: file.name
          };
          setPendingAttachments(prev => [...prev, newAttachment]);
          toast({
            title: "File attached",
            description: `${file.name} is ready to send with your message.`,
          });
        } else {
          await baseHandleFileChange(e);
          toast({
            title: "File attached",
            description: `${file.name} is ready to send with your message.`,
          });
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        toast({
          title: "Upload failed",
          description: "There was an error uploading your file",
          variant: "destructive"
        });
      } finally {
        setIsUploading(false);
      }
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  React.useEffect(() => {
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);
  
  return (
    <div className="border-t border-border p-3 bg-background">
      {activeKnowledgeBase && (
        <div className="mb-2 px-2">
          <KnowledgeBaseActiveIndicator 
            knowledgeBaseName={activeKnowledgeBase}
            onClear={clearKnowledgeBase}
          />
        </div>
      )}
      
      {pendingAttachments.length > 0 && (
        <div className="mb-2 px-2">
          <InputAttachmentPreview 
            attachments={pendingAttachments} 
            onRemoveAttachment={handleRemoveAttachment} 
          />
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="icon" className="shrink-0" disabled={isLoading || isUploading}>
              {isUploading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuItem onClick={handleFileSelect} disabled={isUploading || isLoading}>
              <FileText className="mr-2 h-4 w-4" />
              <span>Upload document</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleFileSelect} disabled={isUploading || isLoading}>
              <Image className="mr-2 h-4 w-4" />
              <span>Upload image</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onOpenCodeEditor} disabled={!onOpenCodeEditor || isLoading || isUploading}>
              <Code className="mr-2 h-4 w-4" />
              <span>Code editor</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onOpenCodeHistory} disabled={!onOpenCodeHistory || isLoading || isUploading}>
              <FileCode className="mr-2 h-4 w-4" />
              <span>Code history</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onOpenCanvas} disabled={!onOpenCanvas || isLoading || isUploading}>
              <Brush className="mr-2 h-4 w-4" />
              <span>Canvas</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => {
              setMessage('/kb ');
              setTimeout(() => {
                if (textareaRef.current) {
                  textareaRef.current.focus();
                  const length = textareaRef.current.value.length;
                  textareaRef.current.setSelectionRange(length, length);
                }
              }, 0);
            }} disabled={isLoading || isUploading}>
              <Book className="mr-2 h-4 w-4" />
              <span>Use knowledge base</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onExportChat} disabled={!onExportChat || isLoading || isUploading}>
              <Download className="mr-2 h-4 w-4" />
              <span>Export chat</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onClearChat} disabled={!onClearChat || isLoading || isUploading} className="text-destructive">
              <Delete className="mr-2 h-4 w-4" />
              <span>Clear chat</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        <div className="flex-1">
          <ChatInputField 
            message={message}
            textareaRef={textareaRef}
            hasAttachments={pendingAttachments.length > 0}
            isLoading={isLoading || isUploading} 
            onTextChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            onSubmit={handleSubmit}
            onSelectKnowledgeBase={handleSelectKnowledgeBase}
          />
        </div>
        
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept="image/*,application/pdf,text/plain,.docx,.csv"
        />
      </form>
      
      {isUploading && (
        <div className="text-xs text-muted-foreground mt-1 px-2 flex items-center">
          <Loader2 size={12} className="animate-spin mr-1" />
          <span>Uploading file...</span>
        </div>
      )}
    </div>
  );
};

export default SimpleChatInput;